import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button } from "antd";

const { Title, Paragraph, Text } = Typography;

const SearchMethods = () => {
  const [isSearching, setIsSearching] = useState(false);
  const [uninformedProgress, setUninformedProgress] = useState(0);
  const [heuristicProgress, setHeuristicProgress] = useState(0);
  const [searchComplete, setSearchComplete] = useState(false);

  // Simple maze - S is start, G is goal
  const maze = [
    ["S", "⬜", "⬜", "⬜", "⬜"],
    ["⬜", "⬛", "⬛", "⬜", "⬜"],
    ["⬜", "⬜", "⬜", "⬜", "⬜"],
    ["⬜", "⬛", "⬛", "⬛", "⬜"],
    ["⬜", "⬜", "⬜", "⬜", "G"],
  ];

  // Uninformed search tries everything (more steps, wandering)
  const uninformedPath = [
    [0, 0],
    [1, 0],
    [2, 0],
    [3, 0],
    [4, 0],
    [4, 1],
    [4, 2],
    [3, 2],
    [2, 2],
    [2, 1],
    [2, 2],
    [2, 3],
    [2, 4],
    [3, 4],
    [4, 4],
  ];

  // Heuristic search goes more directly toward goal (fewer steps)
  const heuristicPath = [
    [0, 0],
    [1, 0],
    [2, 0],
    [2, 1],
    [2, 2],
    [2, 3],
    [2, 4],
    [3, 4],
    [4, 4],
  ];

  const startSearch = () => {
    setIsSearching(true);
    setSearchComplete(false);
    setUninformedProgress(0);
    setHeuristicProgress(0);

    const interval = setInterval(() => {
      setUninformedProgress((prev) => {
        if (prev < uninformedPath.length - 1) return prev + 1;
        return prev;
      });
      setHeuristicProgress((prev) => {
        if (prev < heuristicPath.length - 1) return prev + 1;
        return prev;
      });
    }, 400);

    setTimeout(() => {
      clearInterval(interval);
      setIsSearching(false);
      setSearchComplete(true);
    }, Math.max(uninformedPath.length, heuristicPath.length) * 400);
  };

  const resetSearch = () => {
    setUninformedProgress(0);
    setHeuristicProgress(0);
    setSearchComplete(false);
    setIsSearching(false);
  };

  const renderMaze = (currentPath, progress) => {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "4px",
          width: "100%",
          maxWidth: "280px",
        }}
      >
        {maze.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const visitedIndex = currentPath.findIndex(
              ([r, c]) => r === rowIndex && c === colIndex
            );
            const isVisited = visitedIndex !== -1 && visitedIndex <= progress;
            const isCurrent =
              currentPath[progress] &&
              currentPath[progress][0] === rowIndex &&
              currentPath[progress][1] === colIndex;

            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                style={{
                  width: "50px",
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  background:
                    cell === "⬛"
                      ? "#374151"
                      : isCurrent
                      ? "#fbbf24"
                      : isVisited
                      ? "#bfdbfe"
                      : "#f3f4f6",
                  borderRadius: "8px",
                  border: isCurrent ? "3px solid #f59e0b" : "2px solid #e5e7eb",
                  transition: "all 0.3s ease",
                  fontWeight: "700",
                }}
              >
                {isCurrent
                  ? "🚶"
                  : cell === "S"
                  ? "🏁"
                  : cell === "G"
                  ? "🎯"
                  : cell === "⬛"
                  ? ""
                  : ""}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Paragraph>
          Both start at 🏁 and need to reach 🎯. Watch which one gets there
          faster!
        </Paragraph>
        <div style={{ textAlign: "center" }}>
          <Space>
            <Button
              type="primary"
              size="large"
              onClick={startSearch}
              disabled={isSearching}
            >
              {isSearching ? "Searching..." : "Start"}
            </Button>
            <Button size="large" onClick={resetSearch}>
              Reset
            </Button>
          </Space>
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Uninformed Search</span>}
          >
            <Paragraph style={{ textAlign: "center", marginBottom: "16px" }}>
              Wandering around randomly
            </Paragraph>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              {renderMaze(uninformedPath, uninformedProgress)}
            </div>
            <Card size="small" style={{ textAlign: "center" }}>
              <Text strong>
                Steps: {uninformedProgress + 1} / {uninformedPath.length}
              </Text>
            </Card>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={<span style={{ color: "#0071e3" }}>Heuristic Search</span>}
          >
            <Paragraph style={{ textAlign: "center", marginBottom: "16px" }}>
              Moving smartly toward the goal
            </Paragraph>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              {renderMaze(heuristicPath, heuristicProgress)}
            </div>
            <Card size="small" style={{ textAlign: "center" }}>
              <Text strong>
                Steps: {heuristicProgress + 1} / {heuristicPath.length}
              </Text>
            </Card>
          </Card>
        </Col>
      </Row>

      {searchComplete && (
        <Card style={{ borderColor: "#52c41a", borderWidth: "2px" }}>
          <div style={{ textAlign: "center" }}>
            <Title level={3} style={{ color: "#52c41a" }}>
              Race Complete!
            </Title>
            <Paragraph style={{ fontSize: "16px", marginBottom: 0 }}>
              <Text strong>Uninformed Search</Text> took{" "}
              <Text strong>{uninformedPath.length} steps</Text>
              <br />
              <Text strong>Heuristic Search</Text> finished in{" "}
              <Text strong>{heuristicPath.length} steps</Text>
              <br />
              <Text strong style={{ fontSize: "18px" }}>
                That's{" "}
                {Math.round(
                  (1 - heuristicPath.length / uninformedPath.length) * 100
                )}
                % fewer steps!
              </Text>
            </Paragraph>
          </div>
        </Card>
      )}
    </Space>
  );

  const renderUninformed = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>What is Uninformed Search?</span>
        }
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Explores randomly without a plan - like looking for your keys by
          checking every spot in your house one by one.
        </Paragraph>
        <ul style={{ paddingLeft: "20px" }}>
          <li>No knowledge about where the goal is</li>
          <li>Checks every possibility systematically</li>
          <li>Slower but guaranteed to find the answer</li>
          <li>Also called "blind search"</li>
        </ul>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>Types of Uninformed Search</span>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>Breadth-First Search (BFS)</Text>
            <div style={{ marginTop: "8px" }}>
              Explores all neighbors at the current level before moving deeper.
              Like checking all rooms on one floor before going to the next
              floor.
            </div>
          </Card>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>Depth-First Search (DFS)</Text>
            <div style={{ marginTop: "8px" }}>
              Goes as deep as possible down one path before backtracking. Like
              exploring one hallway completely before trying another.
            </div>
          </Card>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>Uniform Cost Search</Text>
            <div style={{ marginTop: "8px" }}>
              Always expands the path with the lowest cost so far. Like choosing
              the cheapest route at each step.
            </div>
          </Card>
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            When to Use Uninformed Search
          </span>
        }
      >
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>When you have no information about where the goal might be</li>
          <li>When the search space is small enough to explore everything</li>
          <li>When you need to guarantee finding a solution if one exists</li>
          <li>Simple puzzles or games with limited possibilities</li>
        </ul>
      </Card>
    </Space>
  );

  const renderHeuristic = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>What is Heuristic Search?</span>
        }
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Uses smart clues to search efficiently - like checking where you
          usually leave your keys first (bedroom, kitchen, car).
        </Paragraph>
        <ul style={{ paddingLeft: "20px" }}>
          <li>Uses a "heuristic" (educated guess) to guide the search</li>
          <li>Focuses on promising paths first</li>
          <li>Much faster for large problems</li>
          <li>Also called "informed search"</li>
        </ul>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>Types of Heuristic Search</span>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>A* Search</Text>
            <div style={{ marginTop: "8px" }}>
              Combines actual distance traveled with estimated distance to goal.
              The most popular heuristic search algorithm.
            </div>
          </Card>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>Greedy Best-First Search</Text>
            <div style={{ marginTop: "8px" }}>
              Always moves toward what looks closest to the goal. Fast but might
              not find the optimal path.
            </div>
          </Card>
          <Card size="small" style={{ border: "1px solid #0071e3" }}>
            <Text strong>Hill Climbing</Text>
            <div style={{ marginTop: "8px" }}>
              Always moves in the direction that seems to improve the most. Can
              get stuck at local peaks.
            </div>
          </Card>
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>When to Use Heuristic Search</span>
        }
      >
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>When you have some knowledge about where the goal might be</li>
          <li>When the search space is very large</li>
          <li>When you need fast results (like GPS navigation)</li>
          <li>
            Complex problems where checking everything would take too long
          </li>
        </ul>
      </Card>
    </Space>
  );

  const renderComparison = () => {
    const comparisonData = [
      {
        aspect: "Speed",
        uninformed: "Slower - checks everything",
        heuristic: "Faster - uses smart guesses",
      },
      {
        aspect: "Efficiency",
        uninformed: "Less efficient - wastes steps",
        heuristic: "More efficient - fewer steps",
      },
      {
        aspect: "Knowledge",
        uninformed: "No prior knowledge needed",
        heuristic: "Needs a good heuristic function",
      },
      {
        aspect: "Guarantee",
        uninformed: "Always finds solution",
        heuristic: "Usually finds solution",
      },
      {
        aspect: "Best For",
        uninformed: "Small problems, guaranteed solutions",
        heuristic: "Large problems, fast solutions",
      },
      {
        aspect: "Examples",
        uninformed: "Simple mazes, small puzzles",
        heuristic: "GPS navigation, game AI",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              Search Algorithms Comparison
            </span>
          }
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      borderBottom: "2px solid #0071e3",
                      width: "25%",
                    }}
                  >
                    <Text strong>Aspect</Text>
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      borderBottom: "2px solid #0071e3",
                    }}
                  >
                    <Text strong>Uninformed Search</Text>
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      borderBottom: "2px solid #0071e3",
                    }}
                  >
                    <Text strong>Heuristic Search</Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom:
                        idx < comparisonData.length - 1
                          ? "2px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <td
                      style={{
                        padding: "16px",
                        verticalAlign: "top",
                        textAlign: "left",
                      }}
                    >
                      <Text strong style={{ color: "#0071e3" }}>
                        {row.aspect}
                      </Text>
                    </td>
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        verticalAlign: "top",
                      }}
                    >
                      {row.uninformed}
                    </td>
                    <td
                      style={{
                        padding: "16px",
                        textAlign: "left",
                        verticalAlign: "top",
                      }}
                    >
                      {row.heuristic}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card
          title={<span style={{ color: "#0071e3" }}>Real-World Examples</span>}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card size="small" style={{ border: "1px solid #0071e3" }}>
                <Text strong>Uninformed Search</Text>
                <ul
                  style={{
                    paddingLeft: "20px",
                    marginTop: "8px",
                    marginBottom: 0,
                  }}
                >
                  <li>Solving simple Sudoku puzzles</li>
                  <li>Finding files by checking every folder</li>
                  <li>Testing every possible password</li>
                  <li>Exploring a small dungeon in a video game</li>
                </ul>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" style={{ border: "1px solid #0071e3" }}>
                <Text strong>Heuristic Search</Text>
                <ul
                  style={{
                    paddingLeft: "20px",
                    marginTop: "8px",
                    marginBottom: 0,
                  }}
                >
                  <li>GPS finding the fastest route home</li>
                  <li>Chess AI planning the next move</li>
                  <li>Route planning for delivery trucks</li>
                  <li>Robot navigation in warehouses</li>
                </ul>
              </Card>
            </Col>
          </Row>
        </Card>

        <Card
          title={<span style={{ color: "#0071e3" }}>Why This Matters</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            In real AI systems (like GPS navigation or game-playing), using
            smart search methods saves{" "}
            <Text strong>tons of time and energy</Text>. Instead of checking
            every possible route, heuristic search uses clues (like "move closer
            to the destination") to find answers faster. That's why your GPS can
            calculate routes instantly instead of taking hours!
          </Paragraph>
        </Card>
      </Space>
    );
  };

  const tabItems = [
    {
      key: "uninformed",
      label: "Uninformed Search",
      children: renderUninformed(),
    },
    {
      key: "heuristic",
      label: "Heuristic Search",
      children: renderHeuristic(),
    },
    {
      key: "comparison",
      label: "Comparison",
      children: renderComparison(),
    },
    {
      key: "demo",
      label: "Interactive Demo",
      children: renderDemo(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>How AI Searches: Smart vs. Random</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Watch two different search methods race to find the goal!
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="uninformed" items={tabItems} size="large" />
    </div>
  );
};

export default SearchMethods;
